(function () {
    'use strict';
    const {purchaseSource, recordPurchaseStage} = require('./modules/purchase-funnel.js');

    const DEFAULT_FAILURE_MESSAGE =
        'Não foi possível iniciar o pagamento. Nenhuma cobrança foi feita. Tente novamente.';
    const SESSION_FAILURE_MESSAGE =
        'Sua sessão expirou. Nenhuma cobrança foi feita. Atualize a página e entre novamente para continuar.';

    function checkoutFailureMessage(status) {
        return status === 401 ? SESSION_FAILURE_MESSAGE : DEFAULT_FAILURE_MESSAGE;
    }

    function subscriptionSource(windowObject) {
        const rawSource = new windowObject.URLSearchParams(
            windowObject.location.search
        ).get('source');

        return purchaseSource(rawSource);
    }

    function trackPageView(analytics, source, variant, abTestVariant) {
        if (!analytics) {
            return;
        }

        analytics.trackSubscriptionPageView?.(source, abTestVariant);
        analytics.trackSubscriptionFunnelStep?.('view', source, {
            price_variant: variant,
            ab_test_variant: abTestVariant,
        });
        analytics.storeSubscriptionSource?.(source);
    }

    function trackCheckoutStart(analytics, source, variant, abTestVariant) {
        if (!analytics) {
            return;
        }

        analytics.trackSubscriptionButtonClick?.(
            source,
            'main_premium',
            variant,
            abTestVariant
        );
        analytics.trackSubscriptionFunnelStep?.('click', source, {
            button_location: 'main_premium',
            price_variant: variant,
            ab_test_variant: abTestVariant,
        });
        analytics.trackSubscriptionCheckoutStart?.(source, variant, abTestVariant);
        analytics.trackSubscriptionFunnelStep?.('checkout_start', source, {
            price_variant: variant,
            ab_test_variant: abTestVariant,
        });
    }

    function googleAnalyticsValue(windowObject, field, timeoutMs = 500) {
        if (typeof windowObject?.gtag !== 'function') {
            return Promise.resolve(null);
        }

        return new Promise(resolve => {
            let settled = false;
            const finish = value => {
                if (settled) {
                    return;
                }
                settled = true;
                windowObject.clearTimeout?.(timeout);
                resolve(value);
            };
            const timeout = windowObject.setTimeout?.(() => finish(null), timeoutMs);

            try {
                windowObject.gtag('get', 'G-RETE2TW7K7', field, value => finish(value));
            } catch (error) {
                finish(null);
            }
        });
    }

    async function checkoutMeasurementContext(windowObject) {
        const [clientId, sessionId] = await Promise.all([
            googleAnalyticsValue(windowObject, 'client_id'),
            googleAnalyticsValue(windowObject, 'session_id'),
        ]);

        return {
            client_id: typeof clientId === 'string' ? clientId : null,
            session_id: typeof sessionId === 'string' || Number.isInteger(sessionId)
                ? String(sessionId)
                : null,
        };
    }

    function enhanceSubscription(
        root,
        windowObject,
        fetchImplementation,
        analytics = windowObject?.astrolooAnalytics
    ) {
        if (!root || typeof root.querySelector !== 'function') {
            return null;
        }

        const button = root.querySelector('[data-am-subscription-submit]');
        const busyStatus = root.querySelector('[data-am-subscription-busy-status]');
        const errorNotice = root.querySelector('[data-am-subscription-error]');
        const errorMessage = root.querySelector('[data-am-subscription-error-message]');

        if (!button || typeof fetchImplementation !== 'function') {
            return null;
        }

        const variant = root.dataset?.subscriptionVariant || 'original';
        const parsedAbTestVariant = Number.parseInt(
            root.dataset?.subscriptionAbTestVariant || '0',
            10
        );
        const abTestVariant = Number.isFinite(parsedAbTestVariant)
            ? parsedAbTestVariant
            : 0;
        const source = subscriptionSource(windowObject);
        let busy = false;

        function setBusy(nextBusy) {
            busy = nextBusy;
            root.setAttribute('aria-busy', String(nextBusy));
            button.disabled = nextBusy;

            if (nextBusy) {
                button.setAttribute('aria-disabled', 'true');
            } else {
                button.removeAttribute('aria-disabled');
            }

            if (busyStatus) {
                busyStatus.hidden = !nextBusy;
            }
        }

        function clearError() {
            if (errorNotice) {
                errorNotice.hidden = true;
            }

            if (errorMessage) {
                errorMessage.textContent = '';
            }
        }

        function showError(status) {
            if (errorMessage) {
                errorMessage.textContent = checkoutFailureMessage(status);
            }

            if (errorNotice) {
                errorNotice.hidden = false;
                errorNotice.focus?.();
            }
        }

        async function handleSubscription() {
            if (busy) {
                return false;
            }

            clearError();
            setBusy(true);
            trackCheckoutStart(analytics, source, variant, abTestVariant);
            recordPurchaseStage(windowObject, source, 'cta_clicked');

            try {
                const measurementContext = await checkoutMeasurementContext(windowObject);
                const response = await fetchImplementation('/api/subscription/checkout', {
                    method: 'POST',
                    referrerPolicy: 'no-referrer',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        variant,
                        abTestVariant,
                        source,
                        analytics: measurementContext,
                    }),
                });

                if (!response.ok) {
                    const failure = new Error('checkout-request-failed');
                    failure.status = response.status;
                    throw failure;
                }

                const data = await response.json();

                if (!data || typeof data.checkout_url !== 'string') {
                    throw new Error('checkout-response-invalid');
                }

                recordPurchaseStage(windowObject, source, 'checkout_redirected');
                windowObject.location.assign(data.checkout_url);

                return true;
            } catch (error) {
                recordPurchaseStage(windowObject, source, 'checkout_client_failed');
                showError(error?.status);
                setBusy(false);

                return false;
            }
        }

        function handlePageShow() {
            setBusy(false);
        }

        button.addEventListener('click', handleSubscription);
        windowObject.addEventListener?.('pageshow', handlePageShow);
        trackPageView(analytics, source, variant, abTestVariant);

        return {
            handleSubscription,
            destroy() {
                button.removeEventListener('click', handleSubscription);
                windowObject.removeEventListener?.('pageshow', handlePageShow);
            },
        };
    }

    function initSubscriptionPage(documentObject = document, windowObject = window) {
        const root = documentObject.querySelector?.('[data-am-subscription]');
        const fetchImplementation = windowObject.fetch?.bind(windowObject);

        return enhanceSubscription(
            root,
            windowObject,
            fetchImplementation,
            windowObject.astrolooAnalytics
        );
    }

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener(
                'DOMContentLoaded',
                () => initSubscriptionPage(),
                {once: true}
            );
        } else {
            initSubscriptionPage();
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            checkoutFailureMessage,
            checkoutMeasurementContext,
            enhanceSubscription,
            initSubscriptionPage,
        };
    }
})();
